/**
 * MessagesPage - Full DM inbox for NLT Radio
 * Shows all conversations with full chat interface
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  MessageCircle, Search, Send, ArrowLeft, MoreVertical,
  Check, CheckCheck, Clock, User, Loader2, ImagePlus, X, Play
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { messagesApi } from '../api/messages';
import { useAuth } from '../context/AuthContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MessagesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, token } = useAuth();
  const { currentTrack } = useAudioPlayer();
  
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [pendingMedia, setPendingMedia] = useState(null); // { url, mediaType, previewUrl }
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [viewerMedia, setViewerMedia] = useState(null);   // { url, mediaType }
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollingRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load conversations on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadConversations();
    }
  }, [isAuthenticated]);

  // Handle URL params for opening specific conversation
  useEffect(() => {
    const userId = searchParams.get('user');
    if (userId && conversations.length > 0) {
      const convo = conversations.find(c => c.otherUser?.id === userId);
      if (convo) {
        setSelectedUser(convo.otherUser);
      }
    }
  }, [searchParams, conversations]);

  // Load messages when user selected
  useEffect(() => {
    if (selectedUser) {
      loadMessages();
      // Start polling for new messages
      pollingRef.current = setInterval(loadMessages, 3000);
      return () => clearInterval(pollingRef.current);
    }
  }, [selectedUser?.id]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when user selected
  useEffect(() => {
    if (selectedUser) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [selectedUser]);

  const loadConversations = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await messagesApi.getConversations();
      setConversations(data.conversations || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
      if (!silent) setError('Failed to load conversations');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!selectedUser?.id) return;
    
    try {
      const data = await messagesApi.getThread(selectedUser.id);
      const next = data.messages || [];
      // Only update state when the thread actually changed, so polling doesn't
      // re-render the list or trigger a scroll-jump every few seconds.
      setMessages(prev => {
        const prevLast = prev[prev.length - 1];
        const nextLast = next[next.length - 1];
        if (prev.length === next.length && prevLast?.id === nextLast?.id) {
          return prev;
        }
        return next;
      });
      
      // Silently refresh conversation list to update unread counts (no spinner flicker)
      loadConversations(true);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && !pendingMedia) || sending || uploadingMedia || !selectedUser) return;

    setSending(true);
    try {
      await messagesApi.send({
        toUserId: selectedUser.id,
        text: newMessage.trim(),
        ...(pendingMedia ? { mediaUrl: pendingMedia.url, mediaType: pendingMedia.mediaType } : {})
      });
      setNewMessage('');
      if (pendingMedia?.previewUrl) URL.revokeObjectURL(pendingMedia.previewUrl);
      setPendingMedia(null);
      loadMessages();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleMediaSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedUser) return;

    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      setError('Please choose an image or video');
      return;
    }
    const maxMb = isImage ? 10 : 50;
    if (file.size > maxMb * 1024 * 1024) {
      setError(`File too large (max ${maxMb}MB)`);
      return;
    }

    setUploadingMedia(true);
    setError(null);
    const previewUrl = URL.createObjectURL(file);
    try {
      const res = await messagesApi.uploadMedia(file);
      setPendingMedia({ url: res.url, mediaType: res.mediaType, previewUrl });
    } catch (err) {
      console.error('Media upload failed:', err);
      URL.revokeObjectURL(previewUrl);
      setError('Upload failed. Please try again.');
    } finally {
      setUploadingMedia(false);
    }
  };

  const clearPendingMedia = () => {
    if (pendingMedia?.previewUrl) URL.revokeObjectURL(pendingMedia.previewUrl);
    setPendingMedia(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const filteredConversations = conversations.filter(convo => {
    if (!searchQuery) return true;
    const name = convo.otherUser?.name || '';
    const username = convo.otherUser?.username || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase()) ||
           username.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="text-center">
          <MessageCircle className="w-16 h-16 text-[#00F0FF]/50 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Sign in to message</h2>
          <p className="text-white/50 mb-4">Connect with artists and fans</p>
          <button
            onClick={() => navigate('/auth')}
            className="px-6 py-3 bg-gradient-to-r from-[#00F0FF] to-[#FF007F] rounded-full text-white font-semibold"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Conversations List */}
      <div className={`${selectedUser ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 border-r border-white/10`}>
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-white">Messages</h1>
            <button 
              onClick={() => navigate('/')}
              className="p-2 hover:bg-white/10 rounded-lg transition"
            >
              <ArrowLeft className="w-5 h-5 text-white/70" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00F0FF]/50 transition"
            />
          </div>
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-12 px-4">
              <MessageCircle className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/50">No conversations yet</p>
              <p className="text-white/30 text-sm mt-1">Start by messaging an artist</p>
            </div>
          ) : (
            filteredConversations.map((convo) => (
              <div
                key={convo.otherUser?.id || convo.id}
                onClick={() => setSelectedUser(convo.otherUser)}
                className={`flex items-center gap-3 p-4 cursor-pointer transition hover:bg-white/5 ${
                  selectedUser?.id === convo.otherUser?.id ? 'bg-white/10' : ''
                }`}
                data-testid={`conversation-${convo.otherUser?.id}`}
              >
                {/* Avatar */}
                {convo.otherUser?.profilePhoto ? (
                  <img 
                    src={convo.otherUser.profilePhoto} 
                    alt=""
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#FF007F] flex items-center justify-center text-white font-bold">
                    {convo.otherUser?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-white truncate">
                      {convo.otherUser?.name || 'Unknown'}
                    </p>
                    <span className="text-xs text-white/40">
                      {formatTime(convo.lastMessage?.createdAt || convo.updatedAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-white/50 truncate flex-1">
                      {convo.lastMessage?.text || 'No messages yet'}
                    </p>
                    {convo.unreadCount > 0 && (
                      <span className="px-2 py-0.5 bg-[#FF007F] text-white text-xs font-bold rounded-full">
                        {convo.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`${selectedUser ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
        {selectedUser ? (
          <>
            {/* Chat Header */}
            <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-[#0A0A0F]">
              <button
                onClick={() => setSelectedUser(null)}
                className="md:hidden p-2 hover:bg-white/10 rounded-lg transition"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              
              <div 
                className="flex items-center gap-3 flex-1 cursor-pointer"
                onClick={() => navigate(`/profile/${selectedUser.id}`)}
              >
                {selectedUser.profilePhoto ? (
                  <img 
                    src={selectedUser.profilePhoto} 
                    alt=""
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#FF007F] flex items-center justify-center text-white font-bold">
                    {selectedUser.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-white hover:text-[#00F0FF] transition">
                    {selectedUser.name}
                  </p>
                  {selectedUser.username && (
                    <p className="text-xs text-white/40">@{selectedUser.username}</p>
                  )}
                </div>
              </div>

              <button className="p-2 hover:bg-white/10 rounded-lg transition">
                <MoreVertical className="w-5 h-5 text-white/70" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00F0FF]/20 to-[#FF007F]/20 flex items-center justify-center mb-4">
                    <MessageCircle className="w-10 h-10 text-[#00F0FF]" />
                  </div>
                  <p className="text-white/70 font-medium">Start the conversation</p>
                  <p className="text-white/40 text-sm mt-1">Send a message to {selectedUser.name}</p>
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const isOwn = msg.fromUserId === user?.id;
                  return (
                    <div 
                      key={msg.id || idx}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${isOwn ? 'order-2' : ''}`}>
                        {msg.story && (
                          <div
                            className={`mb-1 flex items-center gap-2 px-2.5 py-1.5 rounded-xl bg-white/5 border border-white/10 ${isOwn ? 'ml-auto' : ''}`}
                            data-testid={`message-story-ctx-${msg.id || idx}`}
                          >
                            {msg.story.thumbnail ? (
                              <img src={msg.story.thumbnail} alt="" className="w-8 h-8 rounded-md object-cover shrink-0" />
                            ) : (
                              <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#FF007F]/40 to-[#00F0FF]/30 shrink-0" />
                            )}
                            <span className="text-[11px] text-white/60 leading-tight">
                              ↩ Replied to {isOwn ? 'their' : 'your'} story
                              {msg.story.caption ? <span className="block text-white/40 truncate max-w-[160px]">{msg.story.caption}</span> : null}
                            </span>
                          </div>
                        )}
                        {msg.mediaUrl && (
                          <div
                            className={`mb-1 overflow-hidden rounded-2xl ${isOwn ? 'rounded-br-md' : 'rounded-bl-md'} bg-black/40`}
                            data-testid={`message-media-${msg.id || idx}`}
                          >
                            {msg.mediaType === 'video' ? (
                              <video
                                src={msg.mediaUrl}
                                controls
                                playsInline
                                className="max-h-72 w-full object-contain bg-black"
                                data-testid="message-video"
                              />
                            ) : (
                              <button
                                onClick={() => setViewerMedia({ url: msg.mediaUrl, mediaType: 'image' })}
                                className="block w-full"
                                data-testid="message-image-btn"
                              >
                                <img
                                  src={msg.mediaUrl}
                                  alt="shared"
                                  className="max-h-72 w-full object-cover hover:opacity-90 transition-opacity"
                                />
                              </button>
                            )}
                          </div>
                        )}
                        {msg.text && (
                          <div 
                            className={`px-4 py-3 ${
                              isOwn 
                                ? 'bg-gradient-to-r from-[#00F0FF] to-[#00D4FF] text-black rounded-2xl rounded-br-md' 
                                : 'bg-white/10 text-white rounded-2xl rounded-bl-md'
                            }`}
                          >
                            <p className="text-sm leading-relaxed break-words">{msg.text}</p>
                          </div>
                        )}
                        <div className={`flex items-center gap-1.5 mt-1.5 ${isOwn ? 'justify-end' : ''}`}>
                          <span className={`text-xs ${isOwn ? 'text-white/40' : 'text-white/30'}`}>
                            {formatTime(msg.createdAt)}
                          </span>
                          {isOwn && (
                            msg.read ? (
                              <CheckCheck className="w-3.5 h-3.5 text-[#00F0FF]" />
                            ) : (
                              <Check className="w-3.5 h-3.5 text-white/40" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className={`p-4 border-t border-white/10 bg-[#0A0A0F] ${currentTrack ? 'mb-24' : ''}`}>
              {error && (
                <p className="text-[#FF007F] text-xs mb-2" data-testid="message-error">{error}</p>
              )}
              {(pendingMedia || uploadingMedia) && (
                <div className="mb-3 flex items-center gap-3" data-testid="pending-media-preview">
                  <div className="relative w-20 h-20 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
                    {uploadingMedia ? (
                      <Loader2 className="w-6 h-6 text-[#00F0FF] animate-spin" />
                    ) : pendingMedia.mediaType === 'video' ? (
                      <>
                        <video src={pendingMedia.previewUrl} className="w-full h-full object-cover" />
                        <Play className="absolute w-6 h-6 text-white drop-shadow" />
                      </>
                    ) : (
                      <img src={pendingMedia.previewUrl} alt="preview" className="w-full h-full object-cover" />
                    )}
                    {pendingMedia && !uploadingMedia && (
                      <button
                        onClick={clearPendingMedia}
                        className="absolute top-0.5 right-0.5 p-1 rounded-full bg-black/70 hover:bg-black"
                        data-testid="remove-pending-media"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    )}
                  </div>
                  <span className="text-xs text-white/50">
                    {uploadingMedia ? 'Uploading…' : (pendingMedia.mediaType === 'video' ? 'Video ready — add a caption or send' : 'Photo ready — add a caption or send')}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={handleMediaSelect}
                  data-testid="message-media-input"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingMedia || sending}
                  className="p-3 bg-white/5 border border-white/10 rounded-xl text-white/70 hover:text-[#00F0FF] hover:border-[#00F0FF]/50 disabled:opacity-40 transition"
                  data-testid="attach-media-btn"
                  aria-label="Attach photo or video"
                >
                  <ImagePlus className="w-5 h-5" />
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={pendingMedia ? 'Add a caption…' : 'Type a message...'}
                  className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00F0FF]/50 transition"
                  data-testid="message-input"
                />
                <button
                  onClick={handleSend}
                  disabled={(!newMessage.trim() && !pendingMedia) || sending || uploadingMedia}
                  className="p-3 bg-gradient-to-r from-[#00F0FF] to-[#00D4FF] rounded-xl text-black disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-[#00F0FF]/25 transition"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          // No conversation selected
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#00F0FF]/10 to-[#FF007F]/10 flex items-center justify-center mb-6 ring-1 ring-white/10">
              <MessageCircle className="w-12 h-12 text-[#00F0FF]" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Your Messages</h2>
            <p className="text-white/50 max-w-sm">
              Select a conversation to start chatting or send a message to someone from their profile
            </p>
          </div>
        )}
      </div>

      {viewerMedia && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setViewerMedia(null)}
          data-testid="message-media-viewer"
        >
          <button
            onClick={() => setViewerMedia(null)}
            className="absolute top-5 right-5 p-2 rounded-full bg-white/10 hover:bg-white/20"
            data-testid="close-media-viewer"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={viewerMedia.url}
            alt="full"
            className="max-h-[90vh] max-w-full rounded-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default MessagesPage;
